/**
 * The inline form for a `incompleteFields` question (stage 3): one input per missing field, typed
 * by the server from tri-model (`date`, `number`, `boolean`, `text`, `string`).
 *
 * Submitting is a chat message — "Label: value" lines, one per filled field — which the server
 * parses itself (`parseFieldLines`). No ids, no field names on the wire beyond the label: the same
 * message a user could have typed. Free text in the input line stays possible.
 */
import { useState, type FormEvent } from 'react';

import { buttonVariants } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

import type { QuestionBlockData } from './guideBlocks';

type FormField = NonNullable<QuestionBlockData['fields']>[number];

export interface GuideQuestionFormProps {
    fields: FormField[];
    locale: 'de' | 'en';
    disabled?: boolean;
    /** Called with the message text; returns whether it was accepted. */
    onSubmit: (message: string) => boolean;
}

const TEXT = {
    de: { submit: 'Übernehmen', yes: 'Ja', no: 'Nein', optional: 'optional' },
    en: { submit: 'Apply', yes: 'Yes', no: 'No', optional: 'optional' },
};

/** Builds the "Label: value" lines the server parses; empty values are left out. */
export function formMessage(fields: FormField[], values: Record<string, string>, locale: 'de' | 'en'): string {
    return fields
        .map((field) => {
            const value = (values[field.name] ?? '').trim();
            if (!value) return '';
            const label = field.label[locale] || field.label.de;
            return `${label}: ${value}`;
        })
        .filter(Boolean)
        .join('\n');
}

export function GuideQuestionForm({ fields, locale, disabled, onSubmit }: GuideQuestionFormProps) {
    const [values, setValues] = useState<Record<string, string>>({});
    const t = TEXT[locale];
    const set = (name: string, value: string) => setValues((previous) => ({ ...previous, [name]: value }));

    const missingRequired = fields.some((field) => field.required !== false && !(values[field.name] ?? '').trim());
    const message = formMessage(fields, values, locale);

    const submit = (event: FormEvent) => {
        event.preventDefault();
        if (disabled || missingRequired || !message) return;
        onSubmit(message);
    };

    return (
        <form className='flex flex-col gap-2' data-testid='guide-question-form' onSubmit={submit}>
            {fields.map((field) => {
                const id = `guide-field-${field.name}`;
                const label = field.label[locale] || field.label.de;
                const required = field.required !== false;
                const value = values[field.name] ?? '';
                const common = { id, name: field.name, disabled, required, value };
                return (
                    <label key={field.name} htmlFor={id} className='flex flex-col gap-1 text-xs text-muted-foreground'>
                        <span>
                            {label}
                            {field.unit ? ` (${field.unit})` : ''}
                            {required ? '' : ` · ${t.optional}`}
                        </span>
                        {field.type === 'text' ? (
                            <Textarea {...common} rows={2} className='text-sm' onChange={(e) => set(field.name, e.target.value)} />
                        ) : field.type === 'boolean' ? (
                            <select
                                {...common}
                                className='h-9 rounded-md border border-input bg-background px-3 text-sm'
                                onChange={(e) => set(field.name, e.target.value)}
                            >
                                <option value=''></option>
                                <option value={t.yes}>{t.yes}</option>
                                <option value={t.no}>{t.no}</option>
                            </select>
                        ) : (
                            <Input
                                {...common}
                                type={field.type === 'date' ? 'date' : field.type === 'number' ? 'number' : 'text'}
                                className='h-9 text-sm'
                                onChange={(e) => set(field.name, e.target.value)}
                            />
                        )}
                    </label>
                );
            })}
            <div>
                <button
                    type='submit'
                    disabled={disabled || missingRequired}
                    className={cn(buttonVariants({ variant: 'default', size: 'sm' }))}
                >
                    {t.submit}
                </button>
            </div>
        </form>
    );
}
